import { useMemo, useRef, useState } from "react";
import { useCenterPatientsPaginated } from "../../hooks/centers.hooks";
import type { Center } from "../../types/center.types";
import type { Patient } from "../../types/patient.types";
import { useClickAway } from "@uidotdev/usehooks";
import { Box, Flex, Text } from "@chakra-ui/layout";
import { AddIcon } from "@chakra-ui/icons";
import { format } from "date-fns";

/**
 * Input to select patient
 */
const PatientSelect = ({
	center,
	patientSelected,
	setPatientSelected,
	onNewPatient,
}: {
	center: Center | undefined;
	patientSelected?: Patient;
	setPatientSelected: (patient?: Patient) => void;
	onNewPatient?: () => void;
}) => {
	const patients = useCenterPatientsPaginated({ id: center?.id })(
		1,
		1000000,
		undefined,
		undefined,
		["-created_at"],
	);
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState<string | null>(null);
	const searchBarRef = useRef<HTMLDivElement>(null);
	const folded = useMemo(
		() => patientSelected === undefined && open === false,
		[patientSelected, open],
	);
	const handleOpen = () => {
		if (searchBarRef.current && open === false) {
			setSearch(null);
			setOpen(true);
			searchBarRef.current.innerText = "";
			searchBarRef.current.focus();
		}
	};
	const handleClose = () => {
		if (searchBarRef.current && open === true) {
			setOpen(false);
			searchBarRef.current.innerText = "";
		}
	};
	const parentRef = useClickAway<HTMLDivElement>(handleClose);
	const handleUpdateFilter = () => {
		if (searchBarRef.current) {
			setSearch(
				searchBarRef.current.innerText.trim() === ""
					? null
					: searchBarRef.current.innerText,
			);
		}
	};
	const PatientSelectLine = ({
		value,
		onClick,
		activated = false,
	}: {
		value: Patient;
		onClick: () => void;
		activated?: boolean | null;
	}) => {
		return (
			<Flex
				justifyContent="space-between"
				alignItems="center"
				_hover={{
					bgColor: "brand.100",
					color: "brand.700",
				}}
				bgColor={activated ? "brand.100" : undefined}
				color={activated ? "brand.700" : undefined}
				px={4}
				py={2}
				gap={4}
				onClick={onClick}
			>
				<Text
					lineHeight="24px"
					fontSize="16px"
					fontWeight="500"
					color="gray.900"
					overflow="hidden"
					textOverflow="ellipsis"
					whiteSpace="nowrap"
				>
					{value.firstName}&nbsp;{value.lastName}
				</Text>
				<Text
					lineHeight="18px"
					fontSize="12px"
					fontWeight="500"
					color="gray.500"
				>
					{format(value.birthDate, "dd/MM/yyyy")}
				</Text>
			</Flex>
		);
	};
	return (
		<>
			<Box ref={parentRef} position="relative" onFocus={handleOpen}>
				{/* INPUT */}
				<Box
					w="100%"
					h="40px"
					px="1rem"
					lineHeight="40px"
					borderRadius="md"
					border="solid 1px"
					borderColor="gray.200"
					contentEditable
					ref={searchBarRef}
					onInput={handleUpdateFilter}
				></Box>
				{open === false && patientSelected && (
					<Box
						position="absolute"
						top="0.6rem"
						left="1rem"
						onClick={handleOpen}
					>
						{patientSelected.firstName}&nbsp;
						{patientSelected.lastName}
					</Box>
				)}
				{open === false && patientSelected && (
					<Flex
						position="absolute"
						top="0.6rem"
						right="1rem"
						onClick={handleOpen}
						alignItems="center"
						justifyContent="center"
					>
						<Text fontSize="12px" fontWeight={500} color="gray.500">
							{format(patientSelected.birthDate, "dd/MM/yyyy")}
						</Text>
					</Flex>
				)}
				{/* PLACEHOLDER */}
				<Text
					position="absolute"
					marginBottom="-25px"
					top={folded ? "0.6rem" : "0px"}
					left="5px"
					transform={
						folded
							? "translate(10px,0px) scale(1)"
							: "translate(10px,-10px) scale(0.8)"
					}
					color="gray.500"
					px={1}
					transformOrigin="top left"
					transition="all .2s ease-out"
					pointerEvents="none"
					bg="white"
					w="fit-content"
					h="fit-content"
					zIndex={5}
				>
					Patient
				</Text>
				{/* DROPDOWN */}
				{open && (
					<Flex
						w="100%"
						gap={0}
						height="min(fit-content,400px)"
						maxHeight="400px"
						position="absolute"
						top="44px"
						zIndex={1000}
						bgColor="white"
						direction={"column"}
						borderRadius="md"
						border="solid 1px"
						borderColor="gray.200"
					>
						<Flex
							minHeight={"3rem"}
							justifyContent="center"
							alignItems="center"
							color="brand.700"
							cursor="pointer"
							onClick={() => onNewPatient && onNewPatient()}
						>
							<AddIcon mr={4} />
							Ajouter un patient
						</Flex>
						<Box
							overflow="auto"
							borderBottom="solid 1px"
							borderColor="gray.200"
						>
							{patients &&
								patients.data
									.filter((patient) => {
										if (search === null) {
											return true;
										}
										const firstName = patient.firstName.toLowerCase();
										const lastName = patient.lastName.toLowerCase();
										const fullName = `${firstName} ${lastName}`;
										const birthDate = format(patient.birthDate, "dd/MM/yyyy");
										const searchFor = search.toLowerCase();
										return (
											firstName.includes(searchFor) ||
											lastName.includes(searchFor) ||
											fullName.includes(searchFor) ||
											birthDate.includes(searchFor)
										);
									})
									.map((patient) => (
										<PatientSelectLine
											key={patient.id}
											value={patient}
											onClick={() => {
												setPatientSelected(patient);
												handleClose();
											}}
											activated={
												patientSelected && patient.id === patientSelected.id
											}
										/>
									))}
						</Box>
					</Flex>
				)}
			</Box>
		</>
	);
};

export default PatientSelect;
